import React, { useState, useEffect, useRef } from 'react';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  DsButton,
  DsDialog,
  DsIcon,
  DsEthAvatar,
  DsExplorerLink,
  DsSelectSimple,
} from '@tokenyico/design-system/dist/reactJS';

import { TokenService } from 'services';

import RequestDetails from './RequestDetails';
import RequestStatus from './RequestStatus';
import DvdTransferDetails from './RequestDetails/DvdTransferDetails';
import { datetimeFormat } from '@tokenyico/design-system';
import TableWrapper from 'app/components/TableWrapper/TableWrapper';

const RequestsTableWrapper = ({ setError, currentTab, selectedToken }) => {
  const { t } = useTranslation();
  const dialogRef = useRef();
  const [isDvdTransferRequestViewOpen, setIsDvdTransferRequestViewOpen] = useState(false);
  const [isTransferRequestViewOpen, setIsTransferRequestViewOpen] = useState(false);
  const dvdDialogRef = useRef();
  const [selectedRequest, setSelectedRequest] = useState();
  const [tableState, setTableState] = useState({});
  const [tableData, setTableData] = useState([]);
  const [tableTotalRows, setTableTotalRows] = useState(0);

  const [filter, setFilter] = useState({
    status: null,
  });

  const handleViewRequest = (row) => {
    setSelectedRequest(row);

    if (row && row.type === 'DVD_TRANSFER') {
      dvdDialogRef.current.open();
      setIsDvdTransferRequestViewOpen(true);
    } else {
      dialogRef.current.open();
      setIsTransferRequestViewOpen(true);
    }
  };

  const loadData = async (detail) => {
    setTableState(detail);

    const query = {
      limit: detail.rowsPerPage,
      offset: (detail.currentPage - 1) * detail.rowsPerPage,
      filter: detail.filter,
      sortBy: detail.sortBy,
      sortDirection: detail.sortDirection,
    };
    try {
      if (selectedToken) {
        let response;
        if (currentTab === 0) {
          response = await TokenService.getTokenRequestsMeSent(selectedToken.id, {
            ...query,
          });
        }

        if (currentTab === 1) {
          response = await TokenService.getTokenRequestsMeReceived(selectedToken.id, {
            ...query,
          });
        }

        setTableTotalRows(response.totalItemsFound);
        setTableData(response.items);
      }
    } catch (e) {
      setError(e);
    }
  };

  const resetTable = () => {
    loadData(tableState);
  };

  const header = [
    {
      key: 'viewDetails',
      label: '',
      isSortable: false,
    },

    {
      key: 'type',
      label: t('shared.requestType'),
    },

    {
      key: 'date',
      label: t('shared.lastUpdated'),
    },

    {
      key: 'requester',
      label: t('shared.requester'),
      isSortable: false,
    },

    {
      key: 'requesterEmail',
      label: t('shared.requesterEmail'),
      isSortable: false,
    },

    {
      key: 'status',
      label: t('shared.status'),
      isSortable: false,
    },
  ];

  useEffect(() => {
    loadData(tableState);
    // eslint-disable-next-line
  }, [selectedToken.id, currentTab]);

  return (
    <>
      <p className="_text:xs _text-grey-800">
        {tableTotalRows} {t('shared.requests').toLocaleLowerCase()}
      </p>

      <TableWrapper
        height="100%"
        width="100%"
        header={header}
        data={tableData}
        totalRows={tableTotalRows}
        rowsPerPage={10}
        isSortable
        sortBy="date"
        sortDirection="DESC"
        onFetchData={loadData}
        onResetFilters={() => setFilter({ ...filter, status: null })}
        style={{ height: 'calc(100% - 140px)' }}
        // rendering functions
        noData={() => <EmptyState currentTab={currentTab} />}
        viewDetails={({ row }) => (
          <DsButton
            mode="text"
            icon="view"
            iconSize="20px"
            onClick={() => handleViewRequest(row)}
            class="_p(0)"
          />
        )}
        type={({ value }) => <RequestType type={value} />}
        date={({ row }) => (row.updatedAt ? datetimeFormat(row.updatedAt) : null)}
        requester={({ row }) => (
          <div className="_flex:column">
            {currentTab === 0 && (
              <div className="_flex:align(center)">
                <DsEthAvatar address={get(row, 'params.senderWallet')} size={14} />
                <span className="_m:x(2)">
                  {currentTab === 0 ? `(${t('shared.me')})` : get(row, 'requester')}
                </span>
              </div>
            )}
            {currentTab == 1 && (
              <DsExplorerLink
                address={get(row, 'params.maker')}
                network={selectedToken.blockchainNetwork}
              />
            )}
          </div>
        )}
        requesterEmail={({ value }) => value}
        status={({ value }) => <RequestStatus status={value} />}
      >
        <DsSelectSimple
          isFloatLabel
          isMini
          isClearable
          name="status"
          value={filter.status}
          onDsInput={({ detail }) => setFilter({ ...filter, status: detail })}
          label={t('shared.status')}
          options={[
            { label: '(ALL)', value: '' },
            { label: 'COMPLETED', value: 'COMPLETED' },
            { label: 'INITIATED', value: 'INITIATED' },
            { label: 'PENDING', value: 'PENDING_APPROVAL' },
            { label: 'IN PROGRESS', value: 'IN_PROGRESS' },
            { label: 'CANCELLED', value: 'CANCELLED' },
            { label: 'REJECTED', value: 'REJECTED' },
            { label: 'FAILED', value: 'FAILED' },
          ]}
          className="_m:right(2)"
          style={{ marginTop: '0px' }}
        />
      </TableWrapper>
      <DsDialog
        ref={dvdDialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setIsDvdTransferRequestViewOpen(false)}
      >
        <div>
          {isDvdTransferRequestViewOpen && (
            <DvdTransferDetails
              selectedToken={selectedToken}
              request={selectedRequest}
              onCloseModal={(success) => {
                if (success) {
                  resetTable();
                }
                dvdDialogRef.current.close();
              }}
            />
          )}
        </div>
      </DsDialog>
      <DsDialog
        ref={dialogRef}
        isModal={true}
        isDismissible={true}
        onClosed={() => setIsTransferRequestViewOpen(false)}
      >
        <div>
          {isTransferRequestViewOpen && (
            <RequestDetails
              setError={setError}
              request={selectedRequest}
              token={selectedToken}
              onCloseModal={(success) => {
                if (success) {
                  resetTable();
                }
                dialogRef.current.close();
              }}
            />
          )}
        </div>
      </DsDialog>
    </>
  );
};

const RequestType = ({ type }) => {
  const { t } = useTranslation();

  if (type === 'WHITELIST') {
    return (
      <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
        <DsIcon name="profile-card" size="13px" class="_m:right(2)" />
        <span>{t('shared.qualification')}</span>
      </div>
    );
  }

  if (type === 'TOKEN_RECOVERY') {
    return (
      <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
        <DsIcon name="wallet" size="13px" class="_m:right(2)" />
        <span>{t('shared.tokenRecovery')}</span>
      </div>
    );
  }

  if (type === 'CONDITIONAL_TRANSFER') {
    return (
      <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
        <DsIcon name="transfer" class="_m:right(2)" />
        <span>{t('shared.transfer')}</span>
      </div>
    );
  }

  if (type === 'DVD_TRANSFER') {
    return (
      <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
        <DsIcon name="transfer" class="_m:right(2)" />
        <span>{t('shared.dvdTransfer')}</span>
      </div>
    );
  }
  if (type === 'KYC_CHECK') {
    return (
      <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
        <DsIcon name="profile-card" size="13px" class="_m:right(2)" />
        <span>{t('shared.kycCheck')}</span>
      </div>
    );
  }

  return (
    <div className="_flex-row _text:xs" style={{ alignItems: 'center' }}>
      <span>{type}</span>
    </div>
  );
};

const EmptyState = ({ currentTab }) => {
  const { t } = useTranslation();
  return (
    <div className="empty-state _m:top(4) _m:bottom(5)">
      <div className="empty-state__icon">
        <DsIcon name="detail" size="48px" color="var(--grey-800)" />
      </div>
      <div className="empty-state__title">{t(`emptyState.noRequests.title.${currentTab}`)}</div>
    </div>
  );
};

export default RequestsTableWrapper;
